<script setup lang="ts">
// 1:1 port of the styleguide component `kgr-select-control-with-pills` (plus the `searchable` mode
// of the `kgr-select-control` it wraps). Angular has this once in the styleguide and uses it from
// five call sites (news-form, event-form, task-form, offering-filters, edit-appointment); in Vue it
// had been inlined twice, so this is the extraction §11 asks for.
//
// Interaction, mirroring the original: picking an entry from the dropdown appends it to the value
// and removes it from the remaining options; a pill removes it again. `maximumSelected` disables the
// dropdown once the limit is reached, an option flagged `isExclusive` replaces the whole selection.
import KgrIcon from '@shared/components/KgrIcon.vue';
import { computed, ref } from 'vue';

// Angular binds the whole option object through the form control when no `valueKey` is given
// (offering-filters does that for Ort/Bereich/Feld), so the value type must allow objects.
type OptionValue = string | number | Record<string, any>;
// Deliberately loose: call sites pass their own domain objects (Department, Team, category …).
// `unknown` values would force a cast at every call site, so the index signature stays permissive.
type OptionRecord = Record<string, any> & { isExclusive?: boolean };

const props = withDefaults(
    defineProps<{
        modelValue?: OptionValue[] | null
        options?: (OptionRecord | OptionValue)[]
        /** Property holding the option's value; omit for plain string/number options. */
        valueKey?: string
        /** Property holding the option's label; omit for plain string/number options. */
        labelKey?: string
        /**
         * Angular `[labelTemplate]`: a computed label (offering-filters builds „Ort / Bereich / Feld"
         * that way) or a label that first has to be resolved, like a translatable name. Wins over
         * `labelKey`.
         */
        labelFn?: (option: any) => string
        sortByKey?: string
        emptyLabel?: string
        searchable?: boolean
        block?: boolean
        big?: boolean
        small?: boolean
        maximumSelected?: number
        disabled?: boolean
    }>(),
    { modelValue: () => [], options: () => [], searchable: false, block: false, big: false, small: false, disabled: false },
);

const emit = defineEmits<{ 'update:modelValue': [value: OptionValue[]] }>();

// Angular derives this from its Platform service (`platform.IOS || platform.ANDROID`): on mobile the
// search field is a plain input and the listbox stays a normal dropdown, on desktop the listbox is an
// overlay that is invisible until focused.
const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

const search = ref('');
const dropdownOpen = ref(false);

const selected = computed<OptionValue[]>(() => props.modelValue ?? []);

function valueOf(option: OptionRecord | OptionValue): OptionValue {
    if (props.valueKey === undefined) {
        return option as OptionValue;
    }
    return (option as OptionRecord)[props.valueKey] as OptionValue;
}

function labelOf(option: OptionRecord | OptionValue): string {
    if (props.labelFn !== undefined) {
        return props.labelFn(option);
    }
    if (props.labelKey === undefined) {
        return String(option);
    }
    return String((option as OptionRecord)[props.labelKey] ?? '');
}

function optionFor(value: OptionValue): OptionRecord | OptionValue | undefined {
    return props.options.find((option) => valueOf(option) === value);
}

// Options still selectable: everything not already picked, sorted and — in searchable mode —
// narrowed by the query.
const availableOptions = computed(() => {
    let list = props.options.filter((option) => !selected.value.includes(valueOf(option)));
    if (props.sortByKey !== undefined) {
        const key = props.sortByKey;
        list = [...list].sort((a, b) => String((a as OptionRecord)[key] ?? '').localeCompare(String((b as OptionRecord)[key] ?? '')));
    }
    if (props.searchable && search.value.trim()) {
        const needle = search.value.trim().toLowerCase();
        list = list.filter((option) => labelOf(option).toLowerCase().includes(needle));
    }
    return list;
});

const limitReached = computed(() => props.maximumSelected !== undefined && selected.value.length >= props.maximumSelected);
const selectDisabled = computed(() => props.disabled || limitReached.value);

// Das `<option>` traegt den INDEX in `availableOptions`, nicht den Wert: bei Objekt-Optionen waere
// jeder Wert `"[object Object]"` und jede Auswahl traefe denselben Eintrag.
function add(rawValue: string) {
    if (rawValue === '') {
        return;
    }
    const option = availableOptions.value[Number(rawValue)];
    if (option === undefined) {
        return;
    }
    const value = valueOf(option);
    if (selected.value.includes(value)) {
        return;
    }

    // `isExclusive` replaces the entire selection (Angular `applyExclusiveOption`).
    if (typeof option === 'object' && (option as OptionRecord).isExclusive === true) {
        emit('update:modelValue', [value]);
    } else {
        emit('update:modelValue', [...selected.value, value]);
    }
    search.value = '';
}

function remove(value: OptionValue) {
    emit(
        'update:modelValue',
        selected.value.filter((v) => v !== value),
    );
}
</script>

<template>
    <div
        class="selectWithPills"
        :class="{
            'is-searchable': searchable,
            'is-searchableDropdownOpen': searchable && dropdownOpen,
            'is-mobile': isMobile,
            'is-block': block,
        }"
    >
        <input
            v-if="searchable"
            v-model="search"
            class="searchInput"
            :class="[isMobile ? 'kgr-input' : 'kgr-select', { 'kgr-select--block': block && !isMobile, 'kgr-select--small': small && !isMobile }]"
            :placeholder="$t('Suchen')"
            :disabled="selectDisabled"
            @focus="dropdownOpen = true"
            @blur="dropdownOpen = false"
            @keydown.esc="dropdownOpen = false"
        />
        <select
            class="kgr-select"
            :class="{
                'searchableSelect': searchable,
                'kgr-select--block': block,
                'kgr-select--big': big,
                'kgr-select--small': small,
            }"
            :size="searchable && !isMobile ? 5 : 1"
            :disabled="selectDisabled"
            value=""
            @mousedown="dropdownOpen = true"
            @change="
                add(($event.target as HTMLSelectElement).value);
                ($event.target as HTMLSelectElement).value = '';
            "
        >
            <option
                value=""
                class="kgr-select__option"
            >
                {{ emptyLabel ?? '–' }}
            </option>
            <option
                v-for="(option, index) in availableOptions"
                :key="index"
                :value="String(index)"
                class="kgr-select__option"
            >
                {{ labelOf(option) }}
            </option>
        </select>

        <div
            v-if="selected.length"
            class="multiselectWithPills__selectedValuesWrapper"
        >
            <span
                v-for="(value, index) in selected"
                :key="index"
            >
                <button
                    v-if="optionFor(value) !== undefined"
                    type="button"
                    class="kgr-appliedFilter"
                    :class="{ 'kgr-appliedFilter--big': big }"
                    @click="remove(value)"
                >
                    <span class="kgr-appliedFilter__label">{{ labelOf(optionFor(value)!) }}</span>
                    <KgrIcon
                        name="clear"
                        class="kgr-appliedFilter__close"
                    />
                </button>
            </span>
        </div>
    </div>
</template>

<style scoped lang="scss">
.selectWithPills {
    display: inline-block;

    &.is-block {
        display: block;
    }
}

.multiselectWithPills__selectedValuesWrapper {
    margin-top: var(--kgr-default-margin-tiny);
    text-align: left;
}

// Angular select-control.component.scss, `:host.is-searchable` block: on desktop the listbox is an
// overlay that is invisible and click-through until the search field has focus — at rest only the
// search input is visible.
.is-searchable {
    .searchableSelect {
        border-top-left-radius: 0;
        border-top-right-radius: 0;
    }

    &:not(.is-mobile) {
        position: relative;

        .searchableSelect {
            pointer-events: none;
            opacity: 0;
            transition: opacity 300ms;
            position: absolute;
            z-index: 2;
            top: 100%;
            left: 0;
            width: 100%;
            box-shadow: 1px 1px 4px 0 var(--kgr-color-darken-light);
            border-top-left-radius: 0;
        }
    }

    &.is-searchableDropdownOpen {
        .searchableSelect {
            opacity: 1;
            pointer-events: all;
        }
    }
}

.is-mobile,
.is-searchableDropdownOpen {
    .searchInput {
        border-bottom-right-radius: 0 !important;
        border-bottom-left-radius: 0;
        border-bottom: 1px solid var(--kgr-color-gray-light);
    }
}
</style>
